import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import {FormArray, FormGroup} from '@angular/forms';
import {TasksService} from 'src/app/shared/services/tasks.service';
import {SubtaskListComponent} from '../../subtasks/subtask-list/subtask-list.component';
import {ProjectTaskDialogPanels} from '../project-task-dialog-constants';
import {ProjectsTaskSubtaskButtonFooterComponent} from '../subtask-button-footer/projects-task-subtask-button-footer.component';
import {ProjectsTaskSubtaskEditComponent} from '../subtask-edit/projects-task-subtask-edit.component';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'projects-task-subtasks',
  standalone: true,
  imports: [
    CommonModule,
    ProjectsTaskSubtaskEditComponent,
    SubtaskListComponent,
    ProjectsTaskSubtaskButtonFooterComponent,
  ],
  templateUrl: './projects-task-subtasks.component.html',
  styleUrl: './projects-task-subtasks.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsTaskSubtasksComponent {
  @Input() form: FormGroup;
  @Input() newTask: boolean;
  @Input() isTemplate: boolean;
  @Input() projectId: string;
  @Input() loadingSave: boolean;
  @Input() skipToSubtasks: boolean;
  @Input() editingTask: boolean;

  @Output() changePanel = new EventEmitter<ProjectTaskDialogPanels>();
  @Output() finish = new EventEmitter<void>();

  private tasksService = inject(TasksService);
  private cdr = inject(ChangeDetectorRef);
  view = signal<'firstSubtask' | 'subtask' | 'list'>('firstSubtask');
  hideAddYourFirstSubtask = signal<boolean>(false);
  ProjectTaskDialogPanels = ProjectTaskDialogPanels;
  firstSubtaskEditorContainerMaxHeight = '380px';
  subtaskEditorContainerMaxHeight = '461px';

  get subtaskFormArray(): FormArray {
    return this.form.get('subtasks') as FormArray;
  }

  get subtaskIndex(): number {
    return this.tasksService.getSubtaskEditIndex();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['form'] && this.form) {
      this.updateViewState();
    }
  }

  private updateViewState(): void {
    // Handle existing task in read-only mode
    if (!this.newTask && !this.editingTask) {
      this.setListView();
      return;
    }

    // Handle edit mode
    if (this.editingTask) {
      this.view.set('subtask');
      return;
    }

    // Handle new task creation
    if (this.subtaskFormArray.value.length === 0) {
      this.initializeFirstSubtask();
    } else {
      this.view.set('list');
    }
  }

  private setListView(): void {
    this.view.set('list');
    this.hideAddYourFirstSubtask.set(true);
  }

  private initializeFirstSubtask(): void {
    // Create first subtask with task name
    this.subtaskFormArray.push(this.tasksService.getSubtaskFormGroup());
    this.tasksService.setSubtaskEditIndex(0);

    const taskName = this.form.get('label')?.value;
    if (taskName) {
      this.subtaskFormArray.at(0).get('title')?.setValue(taskName);
    }

    // Use proper change detection instead of setTimeout
    this.cdr.detectChanges();
    this.view.set('firstSubtask');
  }

  onNewTaskBack() {
    if (this.view() !== 'firstSubtask') {
      this.view.set('list');
      return;
    }

    if (this.skipToSubtasks) {
      this.changePanel.emit(ProjectTaskDialogPanels.NewBasicInfo);
      return;
    }

    this.changePanel.emit(this.getPreviousPanel());
  }

  onBack() {
    if (this.skipToSubtasks) {
      this.changePanel.emit(ProjectTaskDialogPanels.NewBasicInfo);
      return;
    }

    this.changePanel.emit(this.getPreviousPanel());
  }

  private getPreviousPanel(): ProjectTaskDialogPanels {
    return this.form.get('triggers')?.value.length > 0
      ? ProjectTaskDialogPanels.NewTriggers
      : ProjectTaskDialogPanels.NewSchedule;
  }

  onFinish() {
    this.view.set('list');
  }

  onEditSubtask(index: number) {
    this.tasksService.setSubtaskEditIndex(index, true);
    if (this.newTask) {
      this.view.set('subtask');
    } else {
      this.changePanel.emit(ProjectTaskDialogPanels.SubtaskEdit);
      this.view.set('subtask');
    }
  }

  onNewSubtask() {
    this.subtaskFormArray.push(this.tasksService.getSubtaskFormGroup());
    this.tasksService.setSubtaskEditIndex(this.subtaskFormArray.length - 1);
    if (this.newTask) {
      this.view.set('subtask');
    } else {
      this.changePanel.emit(ProjectTaskDialogPanels.SubtaskEdit);
    }
  }

  onListFinish() {
    this.finish.emit();
  }
}
